import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { ErrorMessage } from 'formik';
import { useButton, useComboBox, useFilter } from 'react-aria';
import { useComboBoxState } from 'react-stately';
import ListBox from './ListBox';
import Popover from './Popover/Popover';
import LoadingSpinner from '../../LoadingSpinner';
import { ArrowLeft2 } from '../../../assets/icons';
import s from './UncontrolledComboBox.module.scss';
import InputErrorMessage from '../InputErrorMessage';

/**
 * A ComboBox based on react-aria from Adobe
 * Taken from https://react-spectrum.adobe.com/react-aria/useComboBox.html
 * @param {object} props - component props
 * @returns {object} - component jsx
 */
function ComboBox(props) {
  const { label, handleLoadMore, isLoading, isError, isRequired, name } = props;

  const errorMessageId = `${name}-error`;

  const { contains } = useFilter({ sensitivity: 'base' });
  const state = useComboBoxState({
    ...props,
    defaultFilter: contains,
    allowsEmptyCollection: true,
  });

  const buttonRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const listBoxRef = React.useRef(null);
  const popoverRef = React.useRef(null);

  const {
    buttonProps: triggerProps,
    inputProps,
    listBoxProps,
    labelProps,
  } = useComboBox(
    {
      ...props,
      inputRef,
      buttonRef,
      listBoxRef,
      popoverRef,
      id: name,
      'aria-describedby': errorMessageId,
    },
    state
  );

  const { buttonProps } = useButton(triggerProps, buttonRef);

  let arrowClasses = s.arrow;
  if (state.isOpen) {
    arrowClasses = `${arrowClasses} ${s.flippedArrow}`;
  }

  let AdditionalPopOverContent;
  if (isLoading) {
    AdditionalPopOverContent = <LoadingSpinner color="grey" />;
  } else if (state.collection.size === 0) {
    AdditionalPopOverContent = (
      <div>
        <FormattedMessage id="combobox.noResults" />
      </div>
    );
  }

  return (
    <div className={s.outerWrapper}>
      {/* eslint-disable-next-line max-len */}
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control, react/jsx-props-no-spreading */}
      <label {...labelProps} className={isError ? s.errorLabel : ''}>
        {label}
        {isRequired && <span>*</span>}
      </label>
      <div className={s.popOverAnchor}>
        <div className={`${s.innerWrapper} ${isError ? s.errorBorder : ''}`}>
          <input
            /* eslint-disable react/jsx-props-no-spreading */
            {...inputProps}
            ref={inputRef}
            className={s.inputElement}
          />
          {/* eslint-disable-next-line react/button-has-type */}
          <button {...buttonProps} ref={buttonRef} className={s.button}>
            <div aria-hidden="true" className={arrowClasses}>
              <ArrowLeft2 />
            </div>
          </button>
        </div>
        {state.isOpen && (
          <Popover
            popoverRef={popoverRef}
            isOpen={state.isOpen}
            onClose={state.close}
          >
            <div
              role="region"
              aria-live="polite"
              className={
                AdditionalPopOverContent ? s.additionalPopOverContent : ''
              }
            >
              {AdditionalPopOverContent}
            </div>
            <ListBox
              {...listBoxProps}
              listBoxRef={listBoxRef}
              state={state}
              handleLoadMore={handleLoadMore}
            />
          </Popover>
        )}
      </div>
      <ErrorMessage
        id={errorMessageId}
        name={name}
        component={InputErrorMessage}
      />
    </div>
  );
}

ComboBox.defaultProps = {
  isLoading: false,
  isError: false,
  isRequired: false,
};

ComboBox.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  handleLoadMore: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  isRequired: PropTypes.bool,
};

export default ComboBox;
